import Vue from 'vue';
import Router from 'vue-router';
import store from '@/store';

/**
 * Wrapper for lazy-loaded route components that handles chunk loading failures.
 * When a chunk fails to load (e.g., after a deployment when old chunks no longer exist),
 * this will reload the page to fetch the latest version.
 */
function lazyLoadWithRetry(importFn) {
  return () => importFn().catch(error => {
    // Check if this is a chunk loading error (SyntaxError from HTML returned instead of JS)
    const isChunkLoadError = 
      error.name === 'ChunkLoadError' ||
      error.message?.includes('Loading chunk') ||
      error.message?.includes('Unexpected token') ||
      error.name === 'SyntaxError';
    
    if (isChunkLoadError) {
      // Check if we've already tried to reload (prevent infinite reload loops)
      const lastReload = sessionStorage.getItem('chunk_reload_timestamp');
      const now = Date.now();
      
      if (!lastReload || (now - parseInt(lastReload, 10)) > 10000) {
        // Store reload timestamp and refresh the page
        sessionStorage.setItem('chunk_reload_timestamp', now.toString());
        window.location.reload();
        return;
      }
    }
    
    // Re-throw other errors or if we've already tried reloading
    throw error;
  });
}

// Lazy load routes for better initial page load performance
// Each route is split into its own chunk and loaded on-demand
const LoginPage = lazyLoadWithRetry(() => import(/* webpackChunkName: "login" */ '@/views/LoginPage.vue'));
const UserPage = lazyLoadWithRetry(() => import(/* webpackChunkName: "user" */ '@/views/UserPage.vue'));
const PlanPage = lazyLoadWithRetry(() => import(/* webpackChunkName: "plan" */ '@/views/PlanPage.vue'));
const ArtworksPage = lazyLoadWithRetry(() => import(/* webpackChunkName: "artworks" */ '@/views/ArtworksPage.vue'));
const AddArtworkPage = lazyLoadWithRetry(() => import(/* webpackChunkName: "artwork-edit" */ '@/views/AddArtworkPage.vue'));
const ArtworkDetailPage = lazyLoadWithRetry(() => import(/* webpackChunkName: "artwork-detail" */ '@/views/ArtworkDetailPage.vue'));
const CollectionsPage = lazyLoadWithRetry(() => import(/* webpackChunkName: "collections" */ '@/views/CollectionsPage.vue'));
const CollectionEditPage = lazyLoadWithRetry(() => import(/* webpackChunkName: "collection-edit" */ '@/views/CollectionEditPage.vue'));
const CollectionDetailPage = lazyLoadWithRetry(() => import(/* webpackChunkName: "collection-detail" */ '@/views/CollectionDetailPage.vue'));
const AddAnimationPage = lazyLoadWithRetry(() => import(/* webpackChunkName: "animation-edit" */ '@/views/AddAnimationPage.vue'));
const ProcessingPage = lazyLoadWithRetry(() => import(/* webpackChunkName: "processing" */ '@/views/ProcessingPage'));
const PreviewPage = lazyLoadWithRetry(() => import(/* webpackChunkName: "preview" */ '@/views/PreviewPage'));
const SubscriptionsPage = lazyLoadWithRetry(() => import(/* webpackChunkName: "subscriptions" */ '@/views/SubscriptionsPage'));
const PanelsPage = lazyLoadWithRetry(() => import(/* webpackChunkName: "panels" */ '@/views/PanelsPage'));
const PanelsEditPage = lazyLoadWithRetry(() => import(/* webpackChunkName: "panels-edit" */ '@/views/PanelsEditPage'));
const PanelsPageGui = lazyLoadWithRetry(() => import(/* webpackChunkName: "panels-gui" */ '@/views/PanelsPageGui'));
const AboutPage = lazyLoadWithRetry(() => import(/* webpackChunkName: "about" */ '@/views/AboutPage'));
const LandingBridgePage = lazyLoadWithRetry(() => import(/* webpackChunkName: "landing-bridge" */ '@/views/LandingBridgePage.vue'));

Vue.use(Router);

const router = new Router({
  mode: 'history',
  routes: [
    {
      path: '/',
      name: 'marketing-page',
      component: LandingBridgePage
    },
    {
      path: '/login',
      name: 'login-page',
      component: LoginPage
    },
    {
      path: '/user',
      name: 'user-page',
      component: UserPage
    },
    {
      path: '/plan',
      name: 'plan-page',
      component: PlanPage
    },
    {
      path: '/artworks',
      name: 'artworks-page',
      component: ArtworksPage
    },
    // New artwork creation routes (must be before :id routes to match first)
    {
      path: '/artworks/new/artwork',
      name: 'new-artwork-page',
      component: AddArtworkPage
    },
    {
      path: '/artworks/new/animation',
      name: 'new-animation-page',
      component: AddAnimationPage
    },
    {
      path: '/artworks/new/processing',
      name: 'new-processing-page',
      component: ProcessingPage
    },
    // Existing artwork routes
    {
      path: '/artworks/:id',
      name: 'artwork-detail-page',
      component: ArtworkDetailPage
    },
    {
      path: '/artworks/:id/edit/artwork',
      name: 'add-artwork-page',
      component: AddArtworkPage
    },
    {
      path: '/artworks/:id/edit/animation',
      name: 'add-animation-page',
      component: AddAnimationPage
    },
    {
      path: '/artworks/:id/edit/processing',
      name: 'processing-page',
      component: ProcessingPage
    },
    {
      path: '/artworks/:id/preview',
      name: 'artwork-preview-page',
      component: PreviewPage,
      props: true
    },
    {
      path: '/collections',
      name: 'collections-page',
      component: CollectionsPage
    },
    {
      path: '/collections/:id',
      name: 'collection-detail-page',
      component: CollectionDetailPage
    },
    {
      path: '/collections/:id/edit',
      name: 'collection-edit-page',
      component: CollectionEditPage
    },
    {
      path: '/collections/:id/preview',
      name: 'collection-preview-page',
      component: PreviewPage,
      props: true
    },
    {
      path: '/subscriptions',
      name: 'subscriptions-page',
      component: SubscriptionsPage
    },
    {
      path: '/panels-gui',
      name: 'panels-page-gui',
      component: PanelsPageGui
    },
    {
      path: '/panels',
      name: 'panels-page',
      component: PanelsPage
    },
    {
      path: '/panels/:id',
      name: 'panels-edit-page',
      component: PanelsEditPage
    },
    {
      path: '/about',
      name: 'about-page',
      component: AboutPage
    }
  ]
});

const PUBLIC_ROUTE_NAMES = new Set(['marketing-page', 'login-page']);

router.beforeEach((to, from, next) => {
  if (PUBLIC_ROUTE_NAMES.has(to.name)) {
    next();
    return;
  }

  const hasUserSessionInStore = Boolean(store.getters.userId);
  if (!hasUserSessionInStore) {
    next({
      name: 'login-page',
      query: { redirect: to.fullPath },
    });
    return;
  }

  next();
});

export default router;
